import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalLayout } from "@/components/legal/LegalLayout";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Términos y Condiciones — Zyntra Tickets" },
      {
        name: "description",
        content:
          "Términos y condiciones de uso de Zyntra Tickets: cuentas, compra de entradas, reventa, wallet, payouts, responsabilidades y legislación aplicable de Costa Rica.",
      },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "Términos y Condiciones — Zyntra Tickets" },
      {
        property: "og:description",
        content:
          "Reglas de uso de la plataforma Zyntra Tickets para compradores, vendedores y organizadores.",
      },
      { property: "og:type", content: "article" },
    ],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <LegalLayout
      title="Términos y Condiciones"
      subtitle="Reglas de uso de la plataforma Zyntra Tickets para compradores, vendedores y organizadores."
      lastUpdated="23 de abril de 2026"
    >
      <section>
        <h2>1. Aceptación de los términos</h2>
        <p>
          Estos Términos y Condiciones (los “Términos”) regulan el uso de la
          plataforma <strong>Zyntra Tickets</strong> (la “Plataforma”), operada por{" "}
          <strong>Zyntra Events S.R.L.</strong>, sociedad costarricense con domicilio
          en San José, Costa Rica. Al crear una cuenta o utilizar la Plataforma,
          aceptás expresamente estos Términos y nuestra{" "}
          <Link to="/privacy">Política de Privacidad</Link>.
        </p>
      </section>

      <section>
        <h2>2. Descripción del servicio</h2>
        <p>Zyntra Tickets es una plataforma digital que permite:</p>
        <ul>
          <li>
            La <strong>venta primaria</strong> de entradas a eventos publicados por
            organizadores autorizados.
          </li>
          <li>
            La <strong>reventa entre usuarios</strong> de entradas previamente
            adquiridas, dentro de los topes autorizados.
          </li>
          <li>
            Una <strong>wallet digital</strong> donde se acreditan los ingresos por
            reventa.
          </li>
          <li>
            La gestión de <strong>payouts</strong> (retiros) hacia cuentas bancarias
            o SINPE Móvil del usuario.
          </li>
        </ul>
        <p>
          Zyntra Events actúa como <strong>intermediario tecnológico</strong> entre
          organizadores, vendedores y compradores.
        </p>
      </section>

      <section>
        <h2>3. Cuentas de usuario</h2>
        <ul>
          <li>
            Para operar en la Plataforma debés crear una cuenta proporcionando datos
            verídicos, completos y actualizados.
          </li>
          <li>
            Podés registrarte con correo y contraseña o mediante{" "}
            <strong>Google OAuth</strong>. En este último caso, recibimos únicamente
            tu nombre, correo verificado y avatar público.
          </li>
          <li>
            Sos responsable de mantener la confidencialidad de tus credenciales y de
            toda actividad realizada desde tu cuenta.
          </li>
          <li>
            Debés tener al menos <strong>18 años</strong> o contar con autorización
            de tu representante legal para operar.
          </li>
        </ul>
      </section>

      <section>
        <h2>4. Compra de entradas</h2>
        <ul>
          <li>
            Cada entrada incluye un <strong>precio base</strong> definido por el
            organizador y, según el caso, un <strong>cargo por servicio</strong>{" "}
            mostrado de forma transparente antes del pago.
          </li>
          <li>
            Los pagos son procesados por <strong>Stripe, Inc.</strong>; Zyntra Events
            no almacena los datos completos de tarjeta.
          </li>
          <li>
            Una vez confirmada la compra, las entradas se entregan en formato digital
            con un código QR único asociado a tu cuenta.
          </li>
          <li>
            Las entradas son <strong>nominativas</strong>. Cualquier transferencia
            debe realizarse exclusivamente mediante el módulo de reventa.
          </li>
          <li>
            Las políticas de devolución, cancelación y reprogramación son
            responsabilidad del <strong>organizador</strong> del evento.
          </li>
        </ul>
      </section>

      <section>
        <h2>5. Reventa de entradas</h2>
        <ul>
          <li>
            Solo podés revender entradas que efectivamente te pertenezcan y no hayan
            sido validadas.
          </li>
          <li>
            El precio de reventa <strong>no podrá exceder</strong> el tope máximo de
            sobreprecio definido por el organizador o la Plataforma.
          </li>
          <li>
            La Plataforma aplicará las comisiones de procesamiento y servicio
            correspondientes, las cuales se mostrarán antes de confirmar la
            publicación.
          </li>
          <li>
            Al concretarse una venta, la entrada se reasigna automáticamente al
            comprador y se invalida el QR original del vendedor.
          </li>
          <li>
            Está estrictamente prohibida la reventa fuera de la Plataforma o por
            encima del tope autorizado.
          </li>
        </ul>
      </section>

      <section>
        <h2>6. Wallet digital</h2>
        <p>
          La wallet de Zyntra Tickets es una funcionalidad interna que refleja los
          saldos generados por reventas concretadas en la Plataforma.
        </p>
        <ul>
          <li>
            <strong>La wallet no es una cuenta bancaria</strong> ni constituye dinero
            electrónico en los términos de la legislación financiera. Zyntra Events
            no es una entidad financiera ni emisora de medios de pago.
          </li>
          <li>
            Los saldos no devengan intereses y solo pueden destinarse a payouts.
          </li>
          <li>
            Zyntra Events puede retener temporalmente fondos cuando exista sospecha
            de fraude, contracargos, disputas o requerimiento legal.
          </li>
        </ul>
      </section>

      <section>
        <h2>7. Payouts</h2>
        <ul>
          <li>
            Podés solicitar el retiro del saldo disponible en tu wallet hacia una
            cuenta bancaria de tu titularidad o vía SINPE Móvil.
          </li>
          <li>
            Los payouts se procesan dentro de los plazos publicados en la Plataforma
            y pueden requerir verificación de identidad.
          </li>
          <li>
            Es tu responsabilidad mantener actualizados y verídicos tus datos de
            cobro.
          </li>
          <li>
            Las obligaciones tributarias derivadas de los ingresos recibidos son de
            tu exclusiva responsabilidad.
          </li>
        </ul>
      </section>

      <section>
        <h2>8. Responsabilidad del usuario</h2>
        <p>Te comprometés a:</p>
        <ul>
          <li>Usar la Plataforma de forma lícita, ética y conforme a estos Términos.</li>
          <li>No suplantar identidades ni utilizar cuentas de terceros.</li>
          <li>
            No utilizar bots, scrapers o cualquier mecanismo automatizado para
            adquirir entradas.
          </li>
          <li>
            No realizar conductas fraudulentas, lavado de activos, financiamiento
            del terrorismo ni operaciones prohibidas por la legislación aplicable.
          </li>
          <li>
            No vulnerar la seguridad ni la integridad técnica de la Plataforma.
          </li>
        </ul>
      </section>

      <section>
        <h2>9. Limitación de responsabilidad</h2>
        <ul>
          <li>
            Zyntra Events actúa como intermediario tecnológico. La organización,
            calidad, contenido, ejecución, suspensión, reprogramación o cancelación
            del evento son responsabilidad exclusiva del organizador.
          </li>
          <li>
            En la máxima medida permitida por la ley, Zyntra Events no será
            responsable por daños indirectos, lucro cesante, pérdida de datos o
            perjuicios derivados de la imposibilidad de uso de la Plataforma.
          </li>
          <li>
            La Plataforma se ofrece <em>“tal cual”</em>, sin garantías implícitas más
            allá de las exigidas por la legislación de Costa Rica.
          </li>
        </ul>
      </section>

      <section>
        <h2>10. Suspensión y cancelación de cuentas</h2>
        <p>
          Zyntra Events podrá suspender, restringir o cancelar tu cuenta, sin
          responsabilidad ulterior, cuando:
        </p>
        <ul>
          <li>Detectemos un incumplimiento de estos Términos.</li>
          <li>
            Existan indicios razonables de fraude, abuso, contracargos o uso
            indebido.
          </li>
          <li>Lo exija una autoridad competente.</li>
        </ul>
        <p>
          Podés solicitar la baja de tu cuenta en cualquier momento escribiéndonos a{" "}
          <a href="mailto:legal@zyntratickets.com">legal@zyntratickets.com</a>.
        </p>
      </section>

      <section>
        <h2>11. Propiedad intelectual</h2>
        <p>
          Todos los contenidos de la Plataforma —marca, logotipos, diseños,
          interfaces, código fuente, textos y materiales gráficos— son propiedad de
          Zyntra Events S.R.L. o de sus licenciantes y están protegidos por la
          legislación de propiedad intelectual de Costa Rica e internacional. Queda
          prohibida su reproducción total o parcial sin autorización expresa.
        </p>
      </section>

      <section>
        <h2>12. Modificaciones</h2>
        <p>
          Podemos modificar estos Términos en cualquier momento. Los cambios
          materiales serán notificados con al menos quince (15) días de antelación
          mediante correo electrónico o aviso destacado en la Plataforma. El uso
          continuado tras la entrada en vigor implica tu aceptación.
        </p>
      </section>

      <section>
        <h2>13. Legislación aplicable y jurisdicción</h2>
        <p>
          Estos Términos se rigen por la legislación de la <strong>República de Costa Rica</strong>.
          Cualquier controversia se someterá, en primera instancia, a un proceso de
          conciliación de buena fe entre las partes. De no alcanzarse acuerdo, la
          disputa se resolverá ante los <strong>tribunales competentes de San José, Costa Rica</strong>,
          con renuncia expresa a cualquier otro fuero que pudiera corresponder.
        </p>
      </section>

      <section>
        <h2>14. Contacto</h2>
        <p>
          Para consultas sobre estos Términos:{" "}
          <a href="mailto:legal@zyntratickets.com">legal@zyntratickets.com</a>.
        </p>
      </section>
    </LegalLayout>
  );
}
